import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveFooter,
  ResponsiveHeader,
} from '../ui/ResponsiveLayout';
import { SubHeadline } from '../ui/Typography';
import Modal from '../ui/Modal';
import FileUpload from '../ui/FileUpload';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { BankIcon, DocumentsIcon } from '../ui/Icons';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

type IncomeSourceType =
  | 'social_security'
  | 'pension'
  | 'rental_income'
  | 'investment_income'
  | 'business_income'
  | 'annuity'
  | 'other';

type IncomeFrequency = 'monthly' | 'quarterly' | 'annual';

interface IncomeSource {
  id: string;
  type: IncomeSourceType;
  sourceName: string;
  payerInstitution: string;
  frequency: IncomeFrequency;
  approximateAmount?: number;
  notes?: string;
  documents?: AppDocument[];
}

const INCOME_SOURCE_OPTIONS: Array<{
  value: IncomeSourceType;
  label: string;
  description: string;
}> = [
  {
    value: 'social_security',
    label: 'Social Security',
    description: 'Government retirement or disability benefits',
  },
  {
    value: 'pension',
    label: 'Pension',
    description: 'Employer-sponsored retirement income',
  },
  {
    value: 'rental_income',
    label: 'Rental Income',
    description: 'Income from owned properties or leases',
  },
  {
    value: 'investment_income',
    label: 'Investment Income',
    description: 'Dividends, interest, or portfolio distributions',
  },
  {
    value: 'business_income',
    label: 'Business Income',
    description: 'Income from owned or operated businesses',
  },
  {
    value: 'annuity',
    label: 'Annuity',
    description: 'Fixed or variable annuity payments',
  },
  {
    value: 'other',
    label: 'Other Income',
    description: 'Any additional recurring income sources',
  },
];

const FREQUENCY_OPTIONS: Array<{ value: IncomeFrequency; label: string }> = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
];

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const formatCurrencyInput = (value: string) => {
  const digits = value.replace(/[^0-9]/g, '');
  if (!digits) return '';
  return parseInt(digits, 10).toLocaleString();
};

const Frame191: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const [incomeSources, setIncomeSources] = useState<IncomeSource[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingIncomeSourceId, setEditingIncomeSourceId] = useState<string | null>(null);

  const [type, setType] = useState<IncomeSourceType>('social_security');
  const [sourceName, setSourceName] = useState('');
  const [payerInstitution, setPayerInstitution] = useState('');
  const [frequency, setFrequency] = useState<IncomeFrequency>('monthly');
  const [approximateAmount, setApproximateAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  useEffect(() => {
    if (session?.taskProgress?.financial_assets?.answers) {
      const savedIncomeSources =
        session.taskProgress.financial_assets.answers.incomeSources || [];

      if (Array.isArray(savedIncomeSources) && savedIncomeSources.length > 0) {
        const normalized = savedIncomeSources.map((source: IncomeSource) => ({
          ...source,
          documents: normalizeDocuments(source.documents || []),
        }));

        setIncomeSources(normalized);
        return;
      }
    }

    try {
      const backupData = localStorage.getItem('income-sources-backup');
      if (backupData) {
        const parsed = JSON.parse(backupData);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const normalized = parsed.map((source: IncomeSource) => ({
            ...source,
            documents: normalizeDocuments(source.documents || []),
          }));

          setIncomeSources(normalized);
        }
      }
    } catch (error) {
      console.error('[Frame191] Failed to load income sources backup:', error);
    }
  }, [session?.taskProgress?.financial_assets]);

  const persistIncomeSources = async (updatedIncomeSources: IncomeSource[]) => {
    try {
      localStorage.setItem('income-sources-backup', JSON.stringify(updatedIncomeSources));
      localStorage.setItem(
        'income-sources-data-backup',
        JSON.stringify({
          incomeSources: updatedIncomeSources,
          lastUpdated: new Date().toISOString(),
        })
      );
    } catch (error) {
      console.error('[Frame191] Failed to save income sources backup:', error);
    }

    await updateTaskProgress('financial_assets', {
      incomeSources: updatedIncomeSources,
    });

    setIncomeSources(updatedIncomeSources);
  };

  const resetForm = () => {
    setType('social_security');
    setSourceName('');
    setPayerInstitution('');
    setFrequency('monthly');
    setApproximateAmount('');
    setNotes('');
    setDocuments([]);
    setEditingIncomeSourceId(null);
    setIsUploadingDocuments(false);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame191] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const handleOpenModal = (selectedType: IncomeSourceType) => {
    resetForm();
    setType(selectedType);
    setShowModal(true);
  };

  const handleEditIncomeSource = (incomeSource: IncomeSource) => {
    setType(incomeSource.type);
    setSourceName(incomeSource.sourceName);
    setPayerInstitution(incomeSource.payerInstitution);
    setFrequency(incomeSource.frequency);
    setApproximateAmount(
      typeof incomeSource.approximateAmount === 'number'
        ? incomeSource.approximateAmount.toLocaleString()
        : ''
    );
    setNotes(incomeSource.notes || '');
    setDocuments(normalizeDocuments(incomeSource.documents || []));
    setEditingIncomeSourceId(incomeSource.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!sourceName.trim() || !payerInstitution.trim()) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      const amountNumber = approximateAmount
        ? parseFloat(approximateAmount.replace(/,/g, ''))
        : undefined;

      const incomeSourceData: IncomeSource = {
        id: editingIncomeSourceId || Date.now().toString(),
        type,
        sourceName: sourceName.trim(),
        payerInstitution: payerInstitution.trim(),
        frequency,
        approximateAmount:
          typeof amountNumber === 'number' && !Number.isNaN(amountNumber)
            ? amountNumber
            : undefined,
        notes: notes.trim() || undefined,
        documents,
      };

      const updatedIncomeSources = editingIncomeSourceId
        ? incomeSources.map((item) =>
            item.id === editingIncomeSourceId ? incomeSourceData : item
          )
        : [...incomeSources, incomeSourceData];

      await persistIncomeSources(updatedIncomeSources);
      closeModal();
    } catch (error) {
      console.error('[Frame191] Failed to save income source:', error);
      alert('Failed to save income source. Please try again.');
    }
  };

  const handleDeleteIncomeSource = async (incomeSourceId: string) => {
    if (!confirm('Are you sure you want to delete this income source?')) return;

    const sourceToDelete = incomeSources.find((item) => item.id === incomeSourceId);
    const attachedDocs = sourceToDelete?.documents || [];

    for (const doc of attachedDocs) {
      try {
        await documentService.delete(doc.id);
      } catch (error) {
        console.error('[Frame191] Failed to delete document while deleting income source:', error);
      }
    }

    const updatedIncomeSources = incomeSources.filter((item) => item.id !== incomeSourceId);
    await persistIncomeSources(updatedIncomeSources);
  };

  const handleRemoveAttachedDocument = async (
    incomeSourceId: string,
    documentIndex: number
  ) => {
    const source = incomeSources.find((item) => item.id === incomeSourceId);
    const docToRemove = source?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame191] Failed to delete saved document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedIncomeSources = incomeSources.map((item) => {
      if (item.id !== incomeSourceId) return item;
      return {
        ...item,
        documents: normalizeDocuments(item.documents || []).filter(
          (_, index) => index !== documentIndex
        ),
      };
    });

    await persistIncomeSources(updatedIncomeSources);
  };

  const removeDocumentFromForm = async (index: number) => {
    const docToRemove = documents[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame191] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const getTypeLabel = (value: IncomeSourceType) => {
    return INCOME_SOURCE_OPTIONS.find((option) => option.value === value)?.label || value;
  };

  const getFrequencyLabel = (value: IncomeFrequency) => {
    return FREQUENCY_OPTIONS.find((option) => option.value === value)?.label || value;
  };


  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title="Income Sources" />

        <SubHeadline>
          Record recurring income sources such as Social Security, pensions, rental income,
          or investment distributions. This helps your executor understand where money was
          coming from.
        </SubHeadline>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {INCOME_SOURCE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleOpenModal(option.value)}
              className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left"
            >
              <div className="flex items-start gap-4">
                <BankIcon className="w-8 h-8 text-cyan-600 flex-shrink-0" />

                <div>
                  <h3 className="font-medium text-stone-800">{option.label}</h3>
                  <p className="text-sm text-stone-600 mt-1">{option.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {incomeSources.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-medium text-stone-800 mb-4">
              Your Income Sources ({incomeSources.length})
            </h2>

            <div
              className={`space-y-4 ${
                incomeSources.length >= 3 ? 'max-h-56 min-h-0 overflow-y-auto' : ''
              }`}
            >
              {incomeSources.map((incomeSource) => (
                <div
                  key={incomeSource.id}
                  className="bg-white rounded-lg border border-neutral-200 p-4"
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex flex-col gap-2 min-w-0 flex-1 sm:flex-row sm:items-start sm:gap-4">
                      <BankIcon className="w-6 h-6 text-cyan-700 flex-shrink-0" />

                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-stone-800 break-words">
                          {incomeSource.sourceName}
                        </div>

                        <div className="text-sm text-stone-500 mt-1 break-words">
                          {getTypeLabel(incomeSource.type)} • {incomeSource.payerInstitution} •{' '}
                          {getFrequencyLabel(incomeSource.frequency)}
                        </div>

                        {typeof incomeSource.approximateAmount === 'number' && (
                          <div className="text-sm text-stone-600 mt-2 break-words">
                            Approx. Amount: ${incomeSource.approximateAmount.toLocaleString()}
                          </div>
                        )}

                        {incomeSource.notes && (
                          <div className="text-sm text-stone-600 mt-2 break-words">
                            {incomeSource.notes}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0">
                      <button
                        onClick={() => handleEditIncomeSource(incomeSource)}
                        className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteIncomeSource(incomeSource.id)}
                        className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {incomeSource.documents && incomeSource.documents.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <div className="text-sm text-cyan-700 font-medium">
                        Attached Documents ({incomeSource.documents.length})
                      </div>

                      <div className="space-y-2">
                        {normalizeDocuments(incomeSource.documents).map((doc, index) => (
                          <div
                            key={`${incomeSource.id}-doc-${doc.id}-${index}`}
                            className="flex flex-col gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                          >
                            <button
                              type="button"
                              onClick={() => openDocument(doc)}
                              className="flex items-start gap-2 min-w-0 w-full text-left sm:flex-1"
                            >
                              <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                              <span
                                className="text-sm font-medium text-stone-800 truncate"
                                title={doc.name}
                              >
                                {doc.name}
                              </span>
                            </button>

                            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
                              <button
                                type="button"
                                onClick={() => downloadDocument(doc)}
                                className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                              >
                                Download
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveAttachedDocument(incomeSource.id, index)
                                }
                                className="text-red-500 hover:text-red-700 text-xs shrink-0"
                                aria-label={`Remove ${doc.name}`}
                                title="Remove document"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </ResponsiveContent>

      <ResponsiveFooter
        title="Inventory Your Assets"
        subtitle="Add recurring income sources"
        progressPercent={62.5}
        onNext={() => navigate('/frame-190')}
        onBack={() => navigate('/frame-189')}
        nextText="Next"
        backText="Back"
      />

      <Modal
        open={showModal}
        title={editingIncomeSourceId ? 'Edit Income Source' : 'Add Income Source'}
        onClose={closeModal}
        onCancel={closeModal}
        onOk={handleSave}
        okText={editingIncomeSourceId ? 'Save Changes' : 'Add Income Source'}
        cancelText="Cancel"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Income Source Type *
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as IncomeSourceType)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
            >
              {INCOME_SOURCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Source Name *
            </label>
            <input
              type="text"
              value={sourceName}
              onChange={(e) => setSourceName(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="e.g., SSA Retirement Benefit"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Payer / Institution *
            </label>
            <input
              type="text"
              value={payerInstitution}
              onChange={(e) => setPayerInstitution(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="e.g., Social Security Administration"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Frequency *
            </label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as IncomeFrequency)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
            >
              {FREQUENCY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Approx Amount
            </label>
            <input
              type="text"
              value={approximateAmount}
              onChange={(e) => setApproximateAmount(formatCurrencyInput(e.target.value))}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="e.g., 2,500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="Any additional context for your executor"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Upload Documents
            </label>
            <FileUpload
              onUpload={async (newFiles) => {
                if (!newFiles.length) return;

                setIsUploadingDocuments(true);

                try {
                  const uploadedDocs = await Promise.all(
                    newFiles.map((file) => documentService.upload(file))
                  );

                  setDocuments((prev) => [...prev, ...uploadedDocs]);
                } catch (error) {
                  console.error('[Frame191] Failed to upload document(s):', error);
                  alert('Failed to upload one or more documents.');
                } finally {
                  setIsUploadingDocuments(false);
                }
              }}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              multiple
            />

            {isUploadingDocuments && (
              <div className="mt-3 text-sm text-stone-500">
                Uploading documents...
              </div>
            )}

            {documents.length > 0 && (
              <ul className="mt-2 space-y-2">
                {documents.map((doc, index) => (
                  <li
                    key={`${doc.id}-${index}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-stone-600 bg-stone-50 p-2 border border-stone-200 rounded"
                  >
                    <button
                      type="button"
                      onClick={() => openDocument(doc)}
                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
                    >
                      <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0" />
                      <span
                        className="text-sm font-medium text-cyan-700 hover:text-cyan-800 truncate"
                        title={doc.name}
                      >
                        {doc.name}
                      </span>
                    </button>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                      >
                        Download
                      </button>

                      <button
                        type="button"
                        onClick={() => removeDocumentFromForm(index)}
                        className="ml-2 text-red-600 hover:text-red-700 text-xs shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Modal>
    </ResponsiveContainer>
  );
};

export default Frame191;